import React from 'react';
import { Link } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';

/**
 * Draft / boilerplate privacy policy for POC. Replace after legal review.
 */
export default function PrivacyPolicy() {
  const { branding } = useBranding();
  const primary = branding.primaryColor;

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <Link to="/" style={{ ...styles.homeLink, color: primary }}>
          {`← Back to ${branding.companyName}`}
        </Link>
      </header>
      <main style={styles.main}>
        <article style={styles.article}>
          <h1 style={styles.h1}>{`${branding.companyName} — Privacy Policy (Draft)`}</h1>
          <p style={styles.meta}>
            <strong>Last updated:</strong> {new Date().toISOString().slice(0, 10)} ·{' '}
            <strong>Status:</strong>
            {` Boilerplate for proof-of-concept — not final. Your legal team should review and replace this document before production use. The site name and appearance you see (including "${branding.companyName}") come from this deployment's branding settings (for example from your organization's portal configuration).`}
          </p>

          <section style={styles.section}>
            <h2 style={styles.h2}>1. Introduction</h2>
            <p style={styles.p}>
              {`This Privacy Policy describes how we collect, use, store, and protect personal data when you use the ${branding.companyName} customer portal and related services (the "Service"). `}
              We are committed to protecting your privacy and complying with applicable data protection laws,
              including the EU General Data Protection Regulation (&quot;GDPR&quot;) where it applies.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>2. Data controller</h2>
            <p style={styles.p}>
              {`The data controller responsible for your personal data is the legal entity behind this deployment — not necessarily identical to the display name "${branding.companyName}", which is configured for this site. `}
              For questions, contact your administrator or the support channel provided by your organization.
              (Replace this section with your legal entity name, address, and contact details.)
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>3. Information we collect</h2>
            <p style={styles.p}>We may collect categories of information such as:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>
                <strong>Account and contact data:</strong> name, email address, company name, and similar
                identifiers you provide when registering or using the Service.
              </li>
              <li style={styles.li}>
                <strong>Transaction and usage data:</strong> RFQs, orders, cart activity, inventory searches, and
                {` related metadata needed to operate the ${branding.companyName} customer portal.`}
              </li>
              <li style={styles.li}>
                <strong>Technical data:</strong> IP address, browser type, device identifiers, timestamps, and
                diagnostic logs used for security, reliability, and support.
              </li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>4. Legal bases (GDPR)</h2>
            <p style={styles.p}>Where GDPR applies, we rely on one or more of the following legal bases:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>
                <strong>Contract:</strong> processing necessary to provide the Service you request.
              </li>
              <li style={styles.li}>
                <strong>Legitimate interests:</strong> improving the Service, security, fraud prevention, and
                analytics, balanced against your rights.
              </li>
              <li style={styles.li}>
                <strong>Consent:</strong> where required (for example, non-essential cookies or marketing), we ask
                for your consent and you may withdraw it at any time.
              </li>
              <li style={styles.li}>
                <strong>Legal obligation:</strong> where we must retain or disclose data to comply with law.
              </li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>5. Cookies and similar technologies</h2>
            <p style={styles.p}>
              We use cookies and similar storage (such as browser local storage) where needed to operate the
              Service — for example, to maintain your session and remember your cookie choices. If you choose
              &quot;Essential only&quot; in our cookie banner, we limit use to what is strictly necessary to run
              and secure the Service. If you choose &quot;Accept all,&quot; we may also use optional cookies or
              tools for analytics and product improvement, as described when those features are enabled.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>6. How we use information</h2>
            <p style={styles.p}>We use personal data to:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Provide, operate, and improve the Service;</li>
              <li style={styles.li}>Authenticate users and prevent unauthorized access;</li>
              <li style={styles.li}>Process RFQs, orders, and related communications;</li>
              <li style={styles.li}>Comply with law and enforce our terms;</li>
              <li style={styles.li}>Analyze usage in aggregated or de-identified form where appropriate.</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>7. Retention</h2>
            <p style={styles.p}>
              We retain personal data only as long as necessary for the purposes above, including legal,
              accounting, and reporting requirements. Retention periods depend on the type of data and your
              relationship with us; details should be finalized with legal counsel.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>8. Sharing and processors</h2>
            <p style={styles.p}>
              We may share data with subprocessors that help us host, secure, or operate the Service (for example
              cloud infrastructure providers). Such providers process data under contractual safeguards. We do not
              sell your personal data.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>9. International transfers</h2>
            <p style={styles.p}>
              If personal data is transferred outside your country (including outside the EEA), we use
              appropriate safeguards such as standard contractual clauses or other mechanisms required by law,
              as determined by your organization&apos;s legal framework.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>10. Your rights (GDPR and similar laws)</h2>
            <p style={styles.p}>Depending on your location, you may have the right to:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Access, correct, or delete your personal data;</li>
              <li style={styles.li}>Restrict or object to certain processing;</li>
              <li style={styles.li}>Data portability where applicable;</li>
              <li style={styles.li}>Withdraw consent where processing is consent-based;</li>
              <li style={styles.li}>Lodge a complaint with a supervisory authority.</li>
            </ul>
            <p style={styles.p}>
              To exercise rights, contact your organization&apos;s administrator or the contact in section 2.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>11. Security</h2>
            <p style={styles.p}>
              We implement appropriate technical and organizational measures to protect personal data. No method
              of transmission over the Internet is completely secure; we strive to follow industry practices.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>12. Children</h2>
            <p style={styles.p}>
              The Service is not directed at children. We do not knowingly collect personal data from children
              under the age where parental consent is required.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>13. Changes</h2>
            <p style={styles.p}>
              We may update this Privacy Policy from time to time. We will post the updated version and revise
              the &quot;Last updated&quot; date. Material changes may require additional notice under applicable
              law.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>14. Contact</h2>
            <p style={styles.p}>
              For privacy questions, contact your organization&apos;s administrator or insert a dedicated privacy
              email after legal review.
            </p>
          </section>
        </article>
      </main>
      <footer style={styles.footer}>
        <Link to="/" style={{ ...styles.footerLink, color: primary }}>
          {`Return to ${branding.companyName}`}
        </Link>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9fafb',
    color: '#111827',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
  },
  homeLink: {
    fontSize: '15px',
    fontWeight: '600',
    textDecoration: 'none',
  },
  main: {
    flex: 1,
    padding: '32px 24px 48px',
  },
  article: {
    maxWidth: '720px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    padding: '32px 28px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  },
  h1: {
    fontSize: '1.75rem',
    fontWeight: '700',
    margin: '0 0 12px 0',
    lineHeight: 1.2,
  },
  meta: {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: 1.5,
    margin: '0 0 28px 0',
    paddingBottom: '20px',
    borderBottom: '1px solid #e5e7eb',
  },
  section: {
    marginBottom: '24px',
  },
  h2: {
    fontSize: '1.125rem',
    fontWeight: '600',
    margin: '0 0 10px 0',
    color: '#111827',
  },
  p: {
    fontSize: '15px',
    lineHeight: 1.65,
    color: '#374151',
    margin: '0 0 12px 0',
  },
  ul: {
    margin: '0 0 12px 0',
    paddingLeft: '1.25rem',
  },
  li: {
    fontSize: '15px',
    lineHeight: 1.6,
    color: '#374151',
    marginBottom: '8px',
  },
  footer: {
    textAlign: 'center',
    padding: '20px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
  },
  footerLink: {
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
  },
};
